import { useBranding } from "@/hooks/useBranding";
import { useAuth } from "@/hooks/useAuth";
import logoDefault from "@/assets/logo-default.png";
import { Link } from "wouter";
import { LogIn, LayoutDashboard } from "lucide-react";

interface PublicLayoutProps {
  children: React.ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "4xl";
}

export function PublicLayout({
  children,
  maxWidth = "2xl",
}: PublicLayoutProps) {
  const { logoUrl, brandName } = useBranding();
  const { isAuthenticated, user } = useAuth();

  const widthClass = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "4xl": "max-w-4xl",
  }[maxWidth];

  const isAdmin = user?.userType === "admin" || user?.userType === "agent";
  const dashboardHref = isAdmin ? "/admin" : "/portal";

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-4 sm:px-8 py-6">
        <img
          src={logoUrl || logoDefault}
          alt={brandName}
          className="h-10 object-contain"
        />
        {isAuthenticated ? (
          <Link
            href={dashboardHref}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </Link>
        ) : (
          <Link
            href="/login"
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogIn className="w-4 h-4" />
            Sign in
          </Link>
        )}
      </header>
      <main className={`mx-auto ${widthClass} px-4 sm:px-8 pb-12`}>
        {children}
      </main>
    </div>
  );
}
